/**
 * AspectRatioDialog Component
 *
 * Modal dialog for selecting image aspect ratio before generation.
 * Displays 3 options optimized for different social media platforms.
 *
 * Usage:
 * <AspectRatioDialog
 *   open={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   onSelect={(ratio) => handleGenerate(ratio)}
 * />
 */

import { memo, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Square, RectangleVertical, RectangleHorizontal } from 'lucide-react';

// ============================================
// TYPES
// ============================================

interface AspectRatioOption {
  value: '1:1' | '4:5' | '9:16' | '16:9';
  label: string;
  description: string;
  dimensions: string;
  icon: typeof Square;
  platforms: string[];
}

interface AspectRatioDialogProps {
  open: boolean;
  onClose: () => void;
  onSelect: (aspectRatio: '1:1' | '4:5' | '9:16' | '16:9') => void;
  isGenerating?: boolean;
  defaultRatio?: '1:1' | '4:5' | '9:16' | '16:9';
}

// ============================================
// CONSTANTS
// ============================================

const ASPECT_RATIO_OPTIONS: AspectRatioOption[] = [
  {
    value: '1:1',
    label: 'Square',
    description: 'Perfect for Instagram feed and Facebook posts',
    dimensions: '1080 × 1080',
    icon: Square,
    platforms: ['Instagram', 'Facebook'],
  },
  {
    value: '4:5',
    label: 'Portrait',
    description: 'Optimal for Instagram feed vertical posts',
    dimensions: '1080 × 1350',
    icon: RectangleVertical,
    platforms: ['Instagram'],
  },
  {
    value: '9:16',
    label: 'Vertical',
    description: 'Perfect for Instagram Stories, Reels, and TikTok',
    dimensions: '1080 × 1920',
    icon: RectangleVertical,
    platforms: ['Instagram Stories', 'Reels', 'TikTok'],
  },
  {
    value: '16:9',
    label: 'Landscape',
    description: 'Best for Facebook and LinkedIn horizontal posts',
    dimensions: '1200 × 675',
    icon: RectangleHorizontal,
    platforms: ['Facebook', 'LinkedIn'],
  },
];

// ============================================
// COMPONENT
// ============================================

const AspectRatioDialog = ({
  open,
  onClose,
  onSelect,
  isGenerating = false,
  defaultRatio = '9:16',
}: AspectRatioDialogProps) => {
  // ============================================
  // EVENT HANDLERS
  // ============================================

  const handleSelect = useCallback(
    (ratio: '1:1' | '4:5' | '9:16' | '16:9') => {
      onSelect(ratio);
      onClose();
    },
    [onSelect, onClose]
  );

  // ============================================
  // RENDER
  // ============================================

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Choose Image Aspect Ratio</DialogTitle>
          <DialogDescription>
            Select the aspect ratio that best fits your target social media platform
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          {ASPECT_RATIO_OPTIONS.map(option => {
            const Icon = option.icon;
            const isDefault = option.value === defaultRatio;

            return (
              <Button
                key={option.value}
                variant="outline"
                className="h-auto flex-col items-start p-4 hover:bg-accent hover:border-primary"
                onClick={() => handleSelect(option.value)}
                disabled={isGenerating}
              >
                <div className="flex items-center gap-3 w-full">
                  <div className="flex-shrink-0">
                    <Icon className="h-8 w-8 text-muted-foreground" />
                  </div>

                  <div className="flex-1 text-left">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <div className="flex items-center gap-2">
                        <h4 className="font-semibold text-base">{option.label}</h4>
                        {isDefault && (
                          <Badge variant="default" className="text-xs">
                            Recommended
                          </Badge>
                        )}
                      </div>
                      <span className="text-xs text-muted-foreground font-mono">
                        {option.value}
                      </span>
                    </div>

                    <p className="text-sm text-muted-foreground mb-2">{option.description}</p>

                    <div className="flex items-center gap-3 text-xs">
                      <span className="text-muted-foreground">
                        Dimensions: <span className="font-mono">{option.dimensions}</span>
                      </span>
                      <span className="text-muted-foreground">•</span>
                      <span className="text-muted-foreground">{option.platforms.join(', ')}</span>
                    </div>
                  </div>
                </div>
              </Button>
            );
          })}
        </div>

        <div className="flex justify-end">
          <Button variant="ghost" onClick={onClose} disabled={isGenerating}>
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const AspectRatioDialogMemo = memo(AspectRatioDialog);
